import React, { useState, useEffect } from 'react';
import {
    Box, Container, Grid, Card, Typography, Button, Dialog,
    DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
    Chip, IconButton, Tooltip, Avatar, CssBaseline
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faBuildingColumns, faTrashCan, faWallet,
    faMobileScreen, faAt, faMoneyBillTransfer
} from '@fortawesome/free-solid-svg-icons';

// IMPORTANTE: Aquí deberás importar tus funciones reales de conexión a la API
import { getMerchantAccounts, createMerchantAccount, deleteMerchantAccount, getPaymentOptions } from '../../../controllers/company/AccountNumbersController';

export default function MerchantBankAccounts() {
    const [accounts, setAccounts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    
    // Estados del Modal
    const [openModal, setOpenModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Estado del Formulario
    const initialFormState = {
        bank_id: '',
        account_type_id: 1, // 1 por defecto (Corriente)
        payment_method_id: '',
        account_number: '',
        description: ''
    };
    const [formData, setFormData] = useState(initialFormState);

    // --- DATOS SIMULADOS (Reemplazar con llamadas a tu API) ---
    const [banksList, setBankList] = useState([]);
    const [paymentMethodsList, setPaymentMethodsList] = useState([])
    // -----------------------------------------------------------

    useEffect(() => {
        fetchAccounts();
    }, []);

    const fetchAccounts = async () => {
        setIsLoading(true);
        fetchPaymentOptions();
        let response = await getMerchantAccounts();
        if(response.success) setAccounts(response.data.accounts);
        setIsLoading(false);
    };

    const fetchPaymentOptions = async () => {
        const response = await getPaymentOptions();
        if(response.success)
        {
            setBankList(response.data.banks);
            setPaymentMethodsList(response.data.payment_methods);
        }
    }

    const handleOpenModal = () => {
        setFormData(initialFormState);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async () => {
        if (!formData.bank_id || !formData.payment_method_id || !formData.account_number) return;
        
        setIsSubmitting(true);
        
        // SIMULACIÓN DE GUARDADO (Reemplazar por tu endpoint real)
        let response = await createMerchantAccount(formData);
        if(response.success) { fetchAccounts(); handleCloseModal(); }
        setIsSubmitting(false);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("¿Estás seguro de que deseas eliminar este método de pago?")) return;
        
        // SIMULACIÓN DE BORRADO
        // await deleteMerchantAccount(id);
        
        setAccounts(accounts.filter(acc => acc.id !== id));
    };

    // Función auxiliar para íconos según el método
    const getMethodIcon = (methodName) => {
        if (!methodName) return faWallet;
        const name = methodName.toLowerCase();
        if (name.includes('zelle')) return faAt;
        if (name.includes('móvil') || name.includes('movil')) return faMobileScreen;
        if (name.includes('transferencia')) return faMoneyBillTransfer;
        return faBuildingColumns;
    };

    return (
        <Box sx={{ minHeight: '100vh', py: { xs: 4, md: 8 }, px: 2 }}>
            <CssBaseline />
            <Container maxWidth="xl">
                
                {/* --- HEADER --- */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 5, gap: 2 }}>
                    <Box>
                        <Typography variant="h4" fontWeight="800" color="#111" gutterBottom>
                            Métodos de Pago
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Gestiona las cuentas bancarias donde recibirás los pagos de tus clientes.
                        </Typography>
                    </Box>
                    <Button 
                        variant="contained" 
                        size="large"
                        startIcon={<FontAwesomeIcon icon={faPlus} />}
                        onClick={handleOpenModal}
                        disableElevation
                        sx={{ borderRadius: 3, fontWeight: 'bold', textTransform: 'none', px: 3 }}
                    >
                        Nueva Cuenta
                    </Button>
                </Box>

                {/* --- CONTENIDO PRINCIPAL --- */}
                {isLoading ? (
                    <Typography textAlign="center" color="text.secondary" py={5}>Cargando tus cuentas...</Typography>
                ) : accounts.length === 0 ? (
                    /* EMPTY STATE */
                    <Card sx={{ p: 6, textAlign: 'center', borderRadius: 4, border: '2px dashed #e0e0e0', boxShadow: 'none', bgcolor: 'transparent' }}>
                        <Avatar sx={{ width: 80, height: 80, bgcolor: '#e3f2fd', color: 'primary.main', margin: '0 auto', mb: 3 }}>
                            <FontAwesomeIcon icon={faWallet} size="2x" />
                        </Avatar>
                        <Typography variant="h5" fontWeight="bold" color="#333" gutterBottom>
                            Aún no tienes cuentas registradas
                        </Typography>
                        <Typography variant="body1" color="text.secondary" mb={4}>
                            Agrega al menos un método de pago para que tus clientes sepan a dónde transferirte.
                        </Typography>
                        <Button variant="outlined" startIcon={<FontAwesomeIcon icon={faPlus} />} onClick={handleOpenModal} sx={{ borderRadius: 3, fontWeight: 'bold', textTransform: 'none' }}>
                            Agregar mi primera cuenta
                        </Button>
                    </Card>
                ) : (
                    /* GRILLA DE TARJETAS */
                    <Grid container spacing={3}>
                        {accounts.map((acc) => (
                            <Grid item xs={12} sm={6} md={4} key={acc.id}>
                                <Card sx={{ p: 3, borderRadius: 4, border: '1px solid #e0e0e0', boxShadow: '0 4px 12px rgba(0,0,0,0.03)', position: 'relative', transition: '0.2s', '&:hover': { borderColor: 'primary.main', transform: 'translateY(-4px)' } }}>
                                    
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Avatar sx={{ bgcolor: '#f0f4f8', color: '#111', width: 45, height: 45 }}>
                                                <FontAwesomeIcon icon={getMethodIcon(acc.payment_method)} />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="subtitle2" color="text.secondary" textTransform="uppercase" fontSize="0.75rem" fontWeight="bold">
                                                    {acc.bank?.name || 'Banco'}
                                                </Typography>
                                                <Typography variant="h6" fontWeight="bold" color="#111" sx={{ lineHeight: 1.2 }}>
                                                    {acc.payment_method}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        {
                                            acc.bank?.name !== 'QUD' &&
                                            <Tooltip title="Eliminar cuenta">
                                                <IconButton size="small" onClick={() => handleDelete(acc.id)} sx={{ color: '#ff5252', bgcolor: '#ffebee', '&:hover': { bgcolor: '#ffcdd2' } }}>
                                                    <FontAwesomeIcon icon={faTrashCan} size="sm" />
                                                </IconButton>
                                            </Tooltip>
                                        }
                                        
                                    </Box>

                                    <Typography variant="body1" fontWeight="600" color="#333" sx={{ letterSpacing: 1, mb: 1 }}>
                                        {acc.account_number}
                                    </Typography>
                                    
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: '70%' }}>
                                            {acc.description || 'Sin alias'}
                                        </Typography>
                                        <Chip label={acc.account_type} size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 'bold', fontSize: '0.7rem' }} />
                                    </Box>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}

                {/* --- MODAL PARA AGREGAR CUENTA --- */}
                <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
                    <DialogTitle sx={{ pb: 1, pt: 3, px: 3 }}>
                        <Typography variant="h5" fontWeight="bold">Agregar Nueva Cuenta</Typography>
                        <Typography variant="body2" color="text.secondary">Ingresa los datos exactos para evitar pagos rechazados.</Typography>
                    </DialogTitle>
                    
                    <DialogContent sx={{ px: 3, pb: 2, pt: '16px !important' }}>
                        <Grid container spacing={2.5}>
                            
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" fontWeight="bold" mb={0.5}>Banco Destino</Typography>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    id='bank_id'
                                    name='bank_id'
                                    value={formData.bank_id} onChange={handleChange}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                >
                                    {banksList.map((bank) => (
                                        <MenuItem key={bank.id} value={bank.id}>{bank.name}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" fontWeight="bold" mb={0.5}>Forma de Pago</Typography>
                                <TextField
                                    select fullWidth size="small" name="payment_method_id"
                                    value={formData.payment_method_id} onChange={handleChange}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                >
                                    {paymentMethodsList.map((method) => (
                                        <MenuItem key={method.id} value={method.id}>{method.name}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="subtitle2" fontWeight="bold" mb={0.5}>Número de Cuenta / Teléfono / Correo</Typography>
                                <TextField
                                    id='account_number'
                                    fullWidth size="small" name="account_number"
                                    placeholder="Ej: 04141234567 o micorreo@zelle.com"
                                    value={formData.account_number} onChange={handleChange}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="subtitle2" fontWeight="bold" mb={0.5}>Alias o Descripción (Opcional)</Typography>
                                <TextField
                                    fullWidth size="small" name="description"
                                    placeholder="Ej: Zelle de la Empresa"
                                    value={formData.description} onChange={handleChange}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                            </Grid>

                        </Grid>
                    </DialogContent>

                    <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
                        <Button onClick={handleCloseModal} sx={{ color: 'text.secondary', fontWeight: 'bold', textTransform: 'none' }}>
                            Cancelar
                        </Button>
                        <LoadingButton 
                            loading={isSubmitting}
                            variant="contained" 
                            onClick={handleSubmit}
                            disabled={!formData.bank_id || !formData.payment_method_id || !formData.account_number}
                            disableElevation
                            sx={{ borderRadius: 2, fontWeight: 'bold', textTransform: 'none', px: 3 }}
                        >
                            Guardar Cuenta
                        </LoadingButton>
                    </DialogActions>
                </Dialog>

            </Container>
        </Box>
    );
}